import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    listPopulation, listPopulationTags, bulkAssignTag,
    createRecipient, updateRecipient,
    deletePopulationTag, createPopulationTag,
    listRecipientTags, assignRecipientTag, removeRecipientTag,
    deleteRecipient, bulkDeleteRecipients,
} from '../utils/populationApi';
import { archiveWithOverwriteCheck } from '../utils/archiveApi';
import ArchiveOverwriteDialog from './ArchiveOverwriteDialog';
import {
    Users, Tag, Plus, X, Pencil, Check, XCircle, ClipboardList,
    ChevronLeft, ChevronRight, Archive, Trash2, Save,
    Download, Upload, FileText,
} from 'lucide-react';
import { showToast } from '../utils/alerts-messages';
import ConfirmModal from './ConfirmModal';
import SamSpinner from './SamSpinner';
// BUG-111: CSV format is owned by csvParser.js so the export header and the
// import parser stay in lockstep. The field list they both read lives in
// recipientFields.js.
import {
    formatUsersCsv,
    parseRecipientsCsv,
    buildRecipientCsvTemplate,
} from '../utils/csvParser';
import { RECIPIENT_CHANNELS as CHANNELS, RECIPIENT_LANGUAGES as LANGUAGES } from '../utils/recipientFields';

const langLabel = (code) => {
    const found = LANGUAGES.find(l => l.code === code);
    return found ? found.label : (code || '—');
};
const channelLabel = (value) => CHANNELS.find(channel => channel.value === value)?.label || value || 'Web';

// The Page-scoped identities come only from DBAM's verified CRM claims. Keep
// them separate from the editable legacy field, which cannot authorize DCM.
export function MessengerIdentityCell({ recipient, legacyEditor = null, formatDate = value => value }) {
    const identities = Array.isArray(recipient.messenger_page_identities)
        ? recipient.messenger_page_identities : [];
    return (
        <div style={{ fontSize: 12, minWidth: 175, wordBreak: 'break-word' }} onClick={event => event.stopPropagation()}>
            {identities.map(identity => (
                <div key={`${identity.sender_binding_id}:${identity.page_id}`} style={{ marginBottom: 7 }}>
                    <div><strong>Page {identity.page_id}</strong></div>
                    <div>PSID: <code>{identity.platform_user_id}</code></div>
                    <div>Binding: <code>{identity.sender_binding_id}</code></div>
                    <div>Last verified: {formatDate(identity.last_verified_at)}</div>
                </div>
            ))}
            {(legacyEditor || recipient.facebook_messenger_id) ? (
                <div style={{ color: '#64748b' }}>
                    <span>Legacy ID (not used for DCM): </span>
                    {legacyEditor || recipient.facebook_messenger_id}
                </div>
            ) : (identities.length === 0 ? '—' : null)}
        </div>
    );
}

/**
 * RecipientBrowser
 * 
 * Full-screen modal for browsing, filtering, and staging recipients from the
 * global population (global_recipients table).
 * 
 * Props:
 *   isOpen - whether the modal is visible
 *   onClose - callback to close
 *   selectedDeploymentId - current deployment to stage users into
 *   onStaged - callback after users are staged (to refresh staging area)
 *   prePopulateSelection - recipient IDs to select when opened
 *   stagedRecipientIds - Set of recipient IDs already in the staging area
 *   onAddToStaging - callback with selected recipient IDs for the new staging workflow
 */
export default function RecipientBrowser({
    isOpen,
    onClose,
    selectedDeploymentId,
    onStaged,
    orgId = '',
    prePopulateSelection,
    stagedRecipientIds,
    onAddToStaging,
}) {
    // Data
    const [recipients, setRecipients] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progressMsg, setProgressMsg] = useState(''); // status text for long operations
    const [error, setError] = useState('');

    // Per-recipient tag map: { recipientId: [tag objects] }
    const [recipientTagMap, setRecipientTagMap] = useState({});

    // Filters
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [searchQ, setSearchQ] = useState('');
    const [filterTag, setFilterTag] = useState('');
    const [filterChannel, setFilterChannel] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [sortBy, setSortBy] = useState('email');
    const [sortOrder, setSortOrder] = useState('ASC');

    // Selection
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectAllInDb, setSelectAllInDb] = useState(false);
    const [selectFilteredSearch, setSelectFilteredSearch] = useState('');
    const [showSelectByTagDropdown, setShowSelectByTagDropdown] = useState(false);

    // Add recipient form
    const [showAddForm, setShowAddForm] = useState(false);
    const [addEmail, setAddEmail] = useState('');
    const [addName, setAddName] = useState('');
    const [addChannel, setAddChannel] = useState('web');
    const [addPhone, setAddPhone] = useState('');
    const [addWhatsApp, setAddWhatsApp] = useState('');
    const [addMessengerId, setAddMessengerId] = useState('');
    const [addLocale, setAddLocale] = useState('');
    const [addPreferredLanguage, setAddPreferredLanguage] = useState('en');
    const [addTimezone, setAddTimezone] = useState('');
    const [addGroup, setAddGroup] = useState('');

    // Bulk language
    const [showSetLangDropdown, setShowSetLangDropdown] = useState(false);
    const [langBulkResult, setLangBulkResult] = useState(null);

    // Inline edit
    const [editingId, setEditingId] = useState(null);
    const [editFields, setEditFields] = useState({});

    // Tag management panel
    const [showTagPanel, setShowTagPanel] = useState(null); // recipient id or 'global'
    const [recipientTags, setRecipientTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#5B9FFF');

    // Delete confirmation (inline, not window.confirm)
    const [confirmDeleteTagId, setConfirmDeleteTagId] = useState(null);

    // Bulk delete recipients confirmation
    const [confirmDeleteCount, setConfirmDeleteCount] = useState(null);
    const [confirmArchiveCount, setConfirmArchiveCount] = useState(null);

    // Archive resync recovery — holds {resolve} while the styled confirmation
    // for re-basing the server staging area on the local archive file is open.
    const [resyncConfirm, setResyncConfirm] = useState(null);
    // Overwrite-consent dialog for recipients already in the open archive (§A4).
    const [overwriteConfirm, setOverwriteConfirm] = useState(null);

    // BUG-111: CSV export / import surfaces. State is local to the browser so
    // ConfirmModal can be reused without lifting state into App.js.
    const [csvImportConfirm, setCsvImportConfirm] = useState(null); // {parsed, dupCount, warnings}
    const [csvImportRunning, setCsvImportRunning] = useState(false);
    const csvFileInputRef = useRef(null);

    const searchRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const prePopulateSelectionRef = useRef(prePopulateSelection);

    useEffect(() => {
        prePopulateSelectionRef.current = prePopulateSelection;
    }, [prePopulateSelection]);

    // ─── Helpers ───
    const fmtDate = (d) => {
        if (!d) return '—';
        try {
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return '—';
            return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch { return '—'; }
    };

    // ─── Load recipients ───
    const loadRecipients = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await listPopulation({
                page, perPage, q: searchQ,
                tag: filterTag, channel: filterChannel, orgId: orgId,
                optedOut: showArchived ? true : undefined,
                staged: false,
                sortBy, sortOrder,
            });
            const items = result.items || [];
            setRecipients(items);
            setPagination(result.pagination || null);

            // Batch-load tags for all visible recipients
            const tagMap = {};
            await Promise.all(items.map(async (r) => {
                try {
                    const rTags = await listRecipientTags(r.global_recipient_id);
                    tagMap[r.global_recipient_id] = rTags;
                } catch {
                    tagMap[r.global_recipient_id] = [];
                }
            }));
            setRecipientTagMap(tagMap);
        } catch (err) {
            setError(err.message || 'Failed to load population');
            setRecipients([]);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, searchQ, filterTag, filterChannel, orgId, showArchived, sortBy, sortOrder]);

    // Load on filter/page change
    useEffect(() => {
        if (isOpen) loadRecipients();
    }, [isOpen, loadRecipients]);

    // Load tags on open
    useEffect(() => {
        if (!isOpen) return;
        listPopulationTags().then(setTags).catch(() => { });
    }, [isOpen]);

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            const initialSelection = prePopulateSelectionRef.current;
            setPage(1);
            setSelectedIds(
                Array.isArray(initialSelection) && initialSelection.length > 0
                    ? new Set(initialSelection)
                    : new Set()
            );
            setEditingId(null);
            setShowAddForm(false);
            setShowTagPanel(null);
            setConfirmDeleteTagId(null);
            setTimeout(() => searchRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Debounced search
    const handleSearchChange = (val) => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setSearchQ(val);
            setPage(1);
        }, 300);
    };

    // Sort
    const handleSort = (col) => {
        if (sortBy === col) {
            setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortOrder('ASC');
        }
        setPage(1);
    };

    // Selection
    const toggleSelect = (id) => {
        setSelectAllInDb(false); // deselecting an individual clears the "all" flag
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (selectedIds.size === recipients.length && recipients.length > 0) {
            setSelectedIds(new Set());
            setSelectAllInDb(false);
        } else {
            setSelectedIds(new Set(recipients.map(r => r.global_recipient_id)));
        }
    };

    // Resolve all matching recipient IDs (for "select all in database" operations)
    const resolveAllIds = async () => {
        if (!selectAllInDb) return [...selectedIds];
        // Fetch all matching IDs from the server using the current filters
        const result = await listPopulation({
            page: 1, perPage: 10000,
            q: searchQ, tag: filterTag, channel: filterChannel, orgId,
            optedOut: showArchived ? true : undefined,
            staged: false, sortBy, sortOrder,
        });
        return (result.items || []).map(r => r.global_recipient_id);
    };



    // Select Filtered: check all recipients whose name or email matches a search string
    const handleSelectFiltered = () => {
        const q = selectFilteredSearch.trim().toLowerCase();
        if (!q) return;
        const matching = recipients.filter(r =>
            (r.email || '').toLowerCase().includes(q) ||
            (r.name || '').toLowerCase().includes(q)
        );
        setSelectedIds(prev => {
            const next = new Set(prev);
            matching.forEach(r => next.add(r.global_recipient_id));
            return next;
        });
        setSelectFilteredSearch('');
    };

    // Select by Tag: check all visible recipients that have a specific tag
    const handleSelectByTag = (tagId) => {
        const matching = recipients.filter(r => {
            const rTags = recipientTagMap[r.global_recipient_id] || [];
            return rTags.some(t => t.tag_id === tagId);
        });
        setSelectedIds(prev => {
            const next = new Set(prev);
            matching.forEach(r => next.add(r.global_recipient_id));
            return next;
        });
        setShowSelectByTagDropdown(false);
    };

    const handleAddToStaging = () => {
        if (selectedIds.size === 0) return;
        const ids = [...selectedIds];
        const metaList = ids
            .map(id => recipients.find(r => r.global_recipient_id === id))
            .filter(Boolean);
        onAddToStaging?.(ids, metaList);
        onClose();
    };

    // Archive selected — export-only. Per archive_design.md §7, recipient
    // archiving writes a snapshot copy to the archive file; nothing is removed
    // from the system. Recipients stay in the population exactly as they were.
    const handleArchiveSelected = async () => {
        if (selectedIds.size === 0 && !selectAllInDb) return;
        setConfirmArchiveCount(null);
        setLoading(true);
        try {
            setProgressMsg('Resolving recipients…');
            const ids = await resolveAllIds();

            // Use the new consolidated staged merge flow, gated on author consent
            // when a recipient already exists in the open archive (§A4).
            setProgressMsg('Staging recipients for archive…');
            const confirmResync = () => new Promise((resolve) => setResyncConfirm({ resolve }));
            const confirmOverwrite = (collisions, filename) =>
                new Promise((resolve) => setOverwriteConfirm({ collisions, filename, resolve }));
            const result = await archiveWithOverwriteCheck({ recipient_ids: ids }, (p) => {
                setProgressMsg(`${p.stage.charAt(0).toUpperCase() + p.stage.slice(1)}: ${p.percent}%`);
            }, { confirmOverwrite, confirmResync });

            setSelectedIds(new Set());
            setSelectAllInDb(false);
            setProgressMsg('');
            await loadRecipients();
            showToast(`Archived ${ids.length} recipient(s). Local archive updated and backup snapshot created on server.`, 'success');
        } catch (err) {
            if (!err?.canceled) setError(err.message);
        } finally {
            setLoading(false);
            setProgressMsg('');
        }
    };

    // Download a blank import template: the header row plus one example row.
    // An author who has never exported still gets the exact column names.
    const handleCsvTemplateDownload = () => {
        const blob = new Blob([buildRecipientCsvTemplate()], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recipient_import_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // BUG-111: CSV export — pulls every recipient matching the current
    // filters (not just the visible page) and writes a download. Uses
    // formatUsersCsv so the column set is the canonical field list in
    // recipientFields.js. Tags are joined in from the per-recipient tag map
    // already loaded into recipientTagMap.
    const handleCsvExport = async () => {
        setLoading(true); setError('');
        try {
            const result = await listPopulation({
                page: 1,
                perPage: 10000, // grab everything matching current filters
                q: searchQ, tag: filterTag, channel: filterChannel, orgId: orgId,
                optedOut: showArchived ? true : undefined,
                staged: false,
                sortBy, sortOrder,
            });
            const items = result.items || [];
            // Merge tags from recipientTagMap when present; otherwise fetch
            // for any recipient not yet loaded in the current page.
            const enriched = await Promise.all(items.map(async (r) => {
                let tags = recipientTagMap[r.global_recipient_id];
                if (!tags) {
                    try { tags = await listRecipientTags(r.global_recipient_id); }
                    catch { tags = []; }
                }
                return { ...r, tags: (tags || []).map((t) => t.tag_name || t.name).filter(Boolean) };
            }));
            const csv = formatUsersCsv(enriched);
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `recipients_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // BUG-111: CSV import — first stage parses and surfaces a confirm dialog
    // showing what will happen (count, duplicate count, any column warnings)
    // so the import isn't a black box. Second stage (confirmCsvImport) is
    // the actual write loop.
    const handleCsvImportFile = async (file) => {
        if (!file) return;
        setError('');
        try {
            const text = await file.text();
            const { recipients: parsed, warnings, errors, rowCount } = parseRecipientsCsv(text);
            if (parsed.length === 0) {
                // Say which rows failed and why. A bare "no valid rows" left the
                // author guessing which cell they had to fix.
                const detail = errors.length > 0
                    ? `\n\n${errors.slice(0, 20).join('\n')}${errors.length > 20 ? `\n…and ${errors.length - 20} more.` : ''}`
                    : '';
                setError(`No rows could be imported from this file.${detail}`);
                return;
            }
            // Count rows whose email already exists in the current org's recipient
            // list. We look at `recipients` state, which is the current page —
            // this is best-effort; on import we still attempt the create and
            // surface server-side duplicate errors per-row.
            const existingEmails = new Set(
                (recipients || []).map((r) => (r.email || '').toLowerCase()).filter(Boolean)
            );
            const dupCount = parsed.filter((p) => existingEmails.has((p.email || '').toLowerCase())).length;
            setCsvImportConfirm({ parsed, dupCount, warnings, errors, rowCount });
        } catch (err) {
            setError(`Could not read CSV: ${err.message}`);
        }
    };

    const confirmCsvImport = async ({ upsertDuplicates }) => {
        const job = csvImportConfirm;
        setCsvImportConfirm(null);
        if (!job) return;
        setCsvImportRunning(true);
        setError('');
        let created = 0;
        let skipped = 0;
        let failed = 0;
        // Tag names in the file that match no existing tag. An import never
        // creates tags, so these are reported at the end rather than dropped
        // without a word.
        const unknownTagNames = new Set();
        const rowFailures = [];
        const tagsByName = new Map((tags || []).map((t) => [t.tag_name?.toLowerCase(), t]));
        try {
            for (const row of job.parsed) {
                const { tags: tagNames, user_id: _u, global_recipient_id: _g, ...payload } = row;
                try {
                    // sam-dbam decides whether this row is a duplicate, and it
                    // decides on email plus name against the whole account.
                    //
                    // This used to be decided here instead, by checking the row
                    // against the page of recipients on screen. That was wrong
                    // twice over. It compared on email alone, so it dropped a
                    // second person who shares an email address. And it only saw
                    // one page, so a duplicate anywhere else was sent anyway —
                    // and sam-dbam then updated it, because the upsert flag was
                    // being thrown away before it reached the database.
                    const createdRow = await createRecipient({ ...payload, upsert: upsertDuplicates });

                    if (createdRow?.skipped) {
                        skipped += 1;
                        continue;
                    }
                    created += 1;

                    // Tag assignment. An import never creates a tag — one typo in
                    // a tag column would otherwise fill the account's tag list
                    // with junk. Names that match nothing are collected and
                    // reported instead.
                    if (Array.isArray(tagNames) && tagNames.length > 0) {
                        const recipientId = createdRow?.global_recipient_id || createdRow?.id;
                        if (recipientId) {
                            for (const tagName of tagNames) {
                                const t = tagsByName.get(tagName.toLowerCase());
                                if (!t) {
                                    unknownTagNames.add(tagName);
                                    continue;
                                }
                                try { await assignRecipientTag(recipientId, t.tag_id || t.id); }
                                catch (_) { /* skip tag failures, surface in summary */ }
                            }
                        }
                    }
                } catch (rowErr) {
                    failed += 1;
                    rowFailures.push(`${payload.email}: ${rowErr.message}`);
                    console.warn('[BUG-111] CSV import row failed:', payload.email, rowErr.message);
                }
            }
            await loadRecipients();
            const lines = [
                `Created or updated: ${created}`,
                `Skipped (duplicate): ${skipped}`,
                `Failed: ${failed}`,
            ];
            if (job.errors?.length > 0) {
                lines.push('', `Rows rejected before import: ${job.errors.length}`, ...job.errors.slice(0, 10));
                if (job.errors.length > 10) lines.push(`…and ${job.errors.length - 10} more.`);
            }
            if (rowFailures.length > 0) {
                lines.push('', 'Rows the server refused:', ...rowFailures.slice(0, 10));
                if (rowFailures.length > 10) lines.push(`…and ${rowFailures.length - 10} more.`);
            }
            if (unknownTagNames.size > 0) {
                lines.push(
                    '',
                    `These tag names do not exist and were not applied: ${[...unknownTagNames].join(', ')}.`,
                    'Create them in Tag Management, then import again.'
                );
            }
            if (job.warnings.length > 0) lines.push('', 'Warnings:', ...job.warnings.slice(0, 10));
            const clean = failed === 0 && !job.errors?.length && unknownTagNames.size === 0;
            showToast(`Import complete.\n\n${lines.join('\n')}`, clean ? 'success' : 'info');
        } catch (err) {
            setError(`Import failed: ${err.message}`);
        } finally {
            setCsvImportRunning(false);
        }
    };

    // Delete selected recipients permanently
    const handleDeleteSelected = async () => {
        if (selectedIds.size === 0 && !selectAllInDb) return;
        setConfirmDeleteCount(null);
        setLoading(true);
        try {
            setProgressMsg('Resolving recipients…');
            const ids = await resolveAllIds();
            setProgressMsg(`Deleting ${ids.length.toLocaleString()} recipients…`);
            await bulkDeleteRecipients(ids);
            setSelectedIds(new Set());
            setSelectAllInDb(false);
            setProgressMsg('');
            await loadRecipients();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setProgressMsg('');
        }
    };



    // ─── Create Recipient ───
    const handleAddRecipient = async () => {
        if (!addEmail.trim()) { setError('Email is required'); return; }
        setLoading(true);
        setError('');
        try {
            await createRecipient({
                email: addEmail.trim(),
                name: addName.trim() || undefined,
                preferred_channel: addChannel,
                phone_number: addPhone.trim() || undefined,
                whatsapp_id: addWhatsApp.trim() || undefined,
                facebook_messenger_id: addMessengerId.trim() || undefined,
                locale: addLocale.trim() || undefined,
                preferred_language: addPreferredLanguage || 'en',
                timezone: addTimezone.trim() || undefined,
                recipient_group: addGroup.trim() || undefined,
            });
            setShowAddForm(false);
            setAddEmail(''); setAddName(''); setAddChannel('web');
            setAddPhone(''); setAddWhatsApp(''); setAddMessengerId(''); setAddLocale(''); setAddPreferredLanguage('en'); setAddTimezone(''); setAddGroup('');
            await loadRecipients();
        } catch (err) {
            setError(err.message || 'Failed to create recipient');
        } finally {
            setLoading(false);
        }
    };

    // ─── Inline Edit ───
    const startEdit = (r) => {
        setEditingId(r.global_recipient_id);
        setEditFields({
            name: r.name || '',
            email: r.email || '',
            preferred_channel: r.preferred_channel || 'web',
            phone_number: r.phone_number || '',
            whatsapp_id: r.whatsapp_id || '',
            facebook_messenger_id: r.facebook_messenger_id || '',
            locale: r.locale || '',
            preferred_language: r.preferred_language || 'en',
            timezone: r.timezone || '',
            recipient_group: r.recipient_group || '',
        });
    };

    const saveEdit = async () => {
        if (!editingId) return;
        setLoading(true);
        try {
            await updateRecipient(editingId, editFields);
            setEditingId(null);
            await loadRecipients();
        } catch (err) {
            setError(err.message || 'Failed to update recipient');
        } finally {
            setLoading(false);
        }
    };

    // ─── Bulk Set Language ───
    const handleSetLanguage = async (lang) => {
        if (selectedIds.size === 0) return;
        setShowSetLangDropdown(false);
        setLangBulkResult(null);
        let done = 0;
        const total = selectedIds.size;
        for (const rid of selectedIds) {
            try {
                await updateRecipient(rid, { preferred_language: lang });
                done++;
            } catch (err) {
                console.warn(`Failed to set language for ${rid}:`, err);
            }
        }
        setLangBulkResult({ done, total, lang });
        await loadRecipients();
    };

    // ─── Per-recipient tag management ───
    const openTagPanel = async (recipientId) => {
        setShowTagPanel(recipientId);
        try {
            const rTags = await listRecipientTags(recipientId);
            setRecipientTags(rTags);
        } catch {
            setRecipientTags([]);
        }
    };

    const handleAssignTag = async (tagId) => {
        if (!showTagPanel || showTagPanel === 'global') return;
        try {
            await assignRecipientTag(showTagPanel, tagId);
            const rTags = await listRecipientTags(showTagPanel);
            setRecipientTags(rTags);
            // Update the tag map for this recipient
            setRecipientTagMap(prev => ({ ...prev, [showTagPanel]: rTags }));
        } catch (err) {
            setError(err.message || 'Failed to assign tag');
        }
    };

    const handleRemoveTag = async (tagId) => {
        if (!showTagPanel || showTagPanel === 'global') return;
        try {
            await removeRecipientTag(showTagPanel, tagId);
            const rTags = await listRecipientTags(showTagPanel);
            setRecipientTags(rTags);
            // Update the tag map for this recipient
            setRecipientTagMap(prev => ({ ...prev, [showTagPanel]: rTags }));
        } catch (err) {
            setError(err.message || 'Failed to remove tag');
        }
    };

    // ─── Global tag management ───
    const handleCreateGlobalTag = async () => {
        if (!newTagName.trim()) return;
        try {
            await createPopulationTag(newTagName.trim(), newTagColor);
            setNewTagName('');
            setNewTagColor('#3b82f6');
            const updated = await listPopulationTags();
            setTags(updated);
        } catch (err) {
            setError(err.message || 'Failed to create tag');
        }
    };

    const handleDeleteGlobalTag = async (tagId) => {
        // Already confirmed — actually delete
        setConfirmDeleteTagId(null);
        try {
            await deletePopulationTag(tagId);
            const updated = await listPopulationTags();
            setTags(updated);
            // Refresh recipient tag map in case tag was in use
            await loadRecipients();
        } catch (err) {
            setError(err.message || 'Failed to delete tag');
        }
    };

    if (!isOpen) return null;

    const totalCount = pagination?.total ?? recipients.length;
    const totalPages = pagination?.total_pages ?? pagination?.totalPages ?? 1;

    const allOnPageSelected = recipients.length > 0 && selectedIds.size === recipients.length;
    const hasMorePages = totalPages > 1;
    const effectiveSelectedCount = selectAllInDb ? totalCount : selectedIds.size;
    const recipientTagIds = new Set(recipientTags.map(t => t.tag_id));

    // Helper to get tag name/color from tag_id
    const getTagInfo = (tagId) => tags.find(t => t.tag_id === tagId);

    // Total column count for loading/empty rows
    const totalCols = 17;

    // Helper for edit field input
    const editInput = (field, placeholder) => (
        <input style={styles.editInput} value={editFields[field] || ''}
            placeholder={placeholder}
            onChange={e => setEditFields(prev => ({ ...prev, [field]: e.target.value }))} />
    );

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Users size={20} color="#5B9FFF" />
                        <h2 style={{ margin: 0, fontSize: 18 }}>Recipients Browser</h2>
                        <span style={styles.badge}>{totalCount.toLocaleString()} recipients</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                            onClick={() => { setShowTagPanel(showTagPanel === 'global' ? null : 'global'); setConfirmDeleteTagId(null); }}
                            style={{ ...styles.headerBtn, background: showTagPanel === 'global' ? '#E3F2FD' : '#F5F6F7' }}
                        >
                            <Tag size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Manage Tags
                        </button>
                        <button
                            onClick={() => { if (selectedIds.size === 0) { showToast('Select one or more recipients to archive.', 'info'); return; } setConfirmArchiveCount(selectedIds.size); }}
                            style={{ ...styles.headerBtn, background: selectedIds.size > 0 ? '#EBF4FF' : '#F5F6F7', color: selectedIds.size > 0 ? '#1a56db' : undefined }}
                        >
                            <Archive size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Archive
                        </button>
                        {/* BUG-111: Export CSV — exports all recipients matching current filters */}
                        <button
                            onClick={handleCsvExport}
                            disabled={loading || csvImportRunning}
                            title="Export all recipients matching current filters to CSV"
                            style={{ ...styles.headerBtn, background: '#F5F6F7' }}
                        >
                            <Download size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Export CSV
                        </button>
                        {/* BUG-111: Import CSV — hidden file input, button triggers it */}
                        <button
                            onClick={() => csvFileInputRef.current?.click()}
                            disabled={loading || csvImportRunning}
                            title="Import recipients from a CSV file"
                            style={{ ...styles.headerBtn, background: '#F5F6F7' }}
                        >
                            <Upload size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> {csvImportRunning ? 'Importing…' : 'Import CSV'}
                        </button>
                        <input
                            ref={csvFileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleCsvImportFile(f);
                                e.target.value = ''; // allow re-selecting the same file
                            }}
                        />
                        {/* Blank template — the column names without having to export first */}
                        <button
                            onClick={handleCsvTemplateDownload}
                            title="Download a blank CSV with every import column and one example row"
                            style={{ ...styles.headerBtn, background: '#F5F6F7' }}
                        >
                            <FileText size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Template
                        </button>
                        <button
                            onClick={() => setShowAddForm(!showAddForm)}
                            style={{ ...styles.headerBtn, background: showAddForm ? '#E8F5E9' : '#F5F6F7' }}
                        >
                            <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Add Recipient
                        </button>
                        <button onClick={onClose} style={styles.closeBtn} title="Close">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Add Recipient Form */}
                {showAddForm && (
                    <div style={styles.addForm}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Email *</label>
                                <input
                                    style={styles.formInput}
                                    type="email" placeholder="email@example.com"
                                    value={addEmail} onChange={e => setAddEmail(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                    autoFocus
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Name</label>
                                <input
                                    style={styles.formInput}
                                    type="text" placeholder="Full name"
                                    value={addName} onChange={e => setAddName(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Phone</label>
                                <input
                                    style={styles.formInput}
                                    type="tel" placeholder="+1 555 123 4567"
                                    value={addPhone} onChange={e => setAddPhone(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>WhatsApp</label>
                                <input
                                    style={styles.formInput}
                                    type="text" placeholder="WhatsApp ID"
                                    value={addWhatsApp} onChange={e => setAddWhatsApp(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Facebook Messenger ID</label>
                                <input
                                    style={styles.formInput}
                                    type="text" placeholder="Page-scoped user ID"
                                    value={addMessengerId} onChange={e => setAddMessengerId(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Group</label>
                                <input
                                    style={styles.formInput}
                                    type="text" placeholder="Group"
                                    value={addGroup} onChange={e => setAddGroup(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && handleAddRecipient()}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Channel</label>
                                <select style={styles.formInput} value={addChannel} onChange={e => setAddChannel(e.target.value)}>
                                    {CHANNELS.map(channel => <option key={channel.value} value={channel.value}>{channel.label}</option>)}
                                </select>
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Locale</label>
                                <input
                                    style={{ ...styles.formInput, width: 80 }}
                                    type="text" placeholder="en-US"
                                    value={addLocale} onChange={e => setAddLocale(e.target.value)}
                                />
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Language</label>
                                <select style={styles.formInput} value={addPreferredLanguage} onChange={e => setAddPreferredLanguage(e.target.value)}>
                                    {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                                </select>
                            </div>
                            <div style={styles.formField}>
                                <label style={styles.formLabel}>Timezone</label>
                                <input
                                    style={{ ...styles.formInput, width: 120 }}
                                    type="text" placeholder="America/New_York"
                                    value={addTimezone} onChange={e => setAddTimezone(e.target.value)}
                                />
                            </div>
                            <button onClick={handleAddRecipient} disabled={loading} style={styles.addBtn}>
                                {loading ? 'Creating…' : 'Create'}
                            </button>
                            <button onClick={() => setShowAddForm(false)} style={styles.cancelBtn}>Cancel</button>
                        </div>
                    </div>
                )}

                {/* Global Tag Management Panel */}
                {showTagPanel === 'global' && (
                    <div style={styles.tagPanel}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Tag size={16} /> Manage Tags
                            </h3>
                            <button onClick={() => { setShowTagPanel(null); setConfirmDeleteTagId(null); }} style={styles.closeSmall}>
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <input
                                style={{ ...styles.formInput, flex: 1 }}
                                placeholder="New tag name"
                                value={newTagName} onChange={e => setNewTagName(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleCreateGlobalTag()}
                            />
                            <input type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)}
                                style={{ width: 36, height: 32, border: '1px solid #DADCE0', borderRadius: 4, cursor: 'pointer' }} />
                            <button onClick={handleCreateGlobalTag} style={styles.addBtn}>Create Tag</button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {tags.length === 0 && <span style={{ color: '#BDC1C6', fontSize: 13 }}>No tags yet</span>}
                            {tags.map(t => (
                                <span key={t.tag_id} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '4px 10px', borderRadius: 12, fontSize: 13,
                                    background: t.tag_color ? `${t.tag_color}22` : '#F5F6F7',
                                    border: `1px solid ${t.tag_color || '#DADCE0'}`,
                                    position: 'relative',
                                }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.tag_color || '#BDC1C6' }} />
                                    {t.tag_name}

                                    {/* Inline delete confirmation */}
                                    {confirmDeleteTagId === t.tag_id ? (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            marginLeft: 4, padding: '2px 6px', borderRadius: 6,
                                            background: '#FFEBEE', border: '1px solid #FFCDD2',
                                            fontSize: 11, whiteSpace: 'nowrap',
                                        }}>
                                            <span style={{ color: '#991b1b' }}>Delete?</span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteGlobalTag(t.tag_id); }}
                                                style={{
                                                    background: '#F44336', color: '#fff', border: 'none',
                                                    borderRadius: 3, padding: '1px 6px', cursor: 'pointer',
                                                    fontSize: 11, fontWeight: 600,
                                                }}>Yes</button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteTagId(null); }}
                                                style={{
                                                    background: '#fff', border: '1px solid #DADCE0',
                                                    borderRadius: 3, padding: '1px 6px', cursor: 'pointer',
                                                    fontSize: 11,
                                                }}>No</button>
                                        </span>
                                    ) : (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteTagId(t.tag_id); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#ef4444', display: 'inline-flex' }}
                                            title="Delete tag"
                                        ><Trash2 size={12} /></button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Per-Recipient Tag Panel */}
                {showTagPanel && showTagPanel !== 'global' && (
                    <div style={styles.tagPanel}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 15 }}>
                                Tags for {recipients.find(r => r.global_recipient_id === showTagPanel)?.email || 'recipient'}
                            </h3>
                            <button onClick={() => setShowTagPanel(null)} style={styles.closeSmall}>
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {tags.map(t => {
                                const assigned = recipientTagIds.has(t.tag_id);
                                return (
                                    <button
                                        key={t.tag_id}
                                        onClick={() => assigned ? handleRemoveTag(t.tag_id) : handleAssignTag(t.tag_id)}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                            padding: '4px 12px', borderRadius: 12, fontSize: 13,
                                            cursor: 'pointer', transition: 'all 0.15s',
                                            background: assigned ? (t.tag_color ? `${t.tag_color}33` : '#e0e7ff') : '#f9fafb',
                                            border: `1.5px solid ${assigned ? (t.tag_color || '#6366f1') : '#E8EAED'}`,
                                            fontWeight: assigned ? 600 : 400,
                                            color: assigned ? '#1e293b' : '#64748b',
                                        }}
                                    >
                                        <span style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: t.tag_color || '#BDC1C6',
                                            opacity: assigned ? 1 : 0.4,
                                        }} />
                                        {t.tag_name}
                                        {assigned && <Check size={12} />}
                                    </button>
                                );
                            })}
                            {tags.length === 0 && (
                                <span style={{ color: '#BDC1C6', fontSize: 13 }}>
                                    No tags available. Create tags in "Manage Tags" first.
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Filters bar */}
                <div style={styles.filterBar}>
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Search email or name…"
                        defaultValue={searchQ}
                        onChange={e => handleSearchChange(e.target.value)}
                        style={styles.searchInput}
                    />
                    <select value={filterTag} onChange={e => { setFilterTag(e.target.value); setPage(1); }} style={styles.select}>
                        <option value="">All tags</option>
                        {tags.map(t => <option key={t.tag_id} value={t.tag_id}>{t.tag_name}</option>)}
                    </select>
                    <select value={filterChannel} onChange={e => { setFilterChannel(e.target.value); setPage(1); }} style={styles.select}>
                        <option value="">All channels</option>
                        {CHANNELS.map(channel => <option key={channel.value} value={channel.value}>{channel.label}</option>)}
                    </select>
                </div>

                {/* Selection Tools */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 20px 6px', flexWrap: 'wrap' }}>
                    {/* Select Filtered */}
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Name or email…"
                            value={selectFilteredSearch}
                            onChange={e => setSelectFilteredSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSelectFiltered(); }}
                            style={{ padding: '3px 8px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, width: 180 }}
                        />
                        <button
                            onClick={handleSelectFiltered}
                            disabled={!selectFilteredSearch.trim()}
                            style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: selectFilteredSearch.trim() ? 'pointer' : 'default', color: selectFilteredSearch.trim() ? '#202124' : '#BDC1C6' }}
                        >Select Matching</button>
                    </div>
                    {/* Select by Tag */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowSelectByTagDropdown(!showSelectByTagDropdown)}
                            style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: 'pointer' }}
                        >Select by Tag ▾</button>
                        {showSelectByTagDropdown && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2, background: '#fff', border: '1px solid #DADCE0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: 160, padding: '4px 0' }}>
                                {tags.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#999' }}>No tags</div>}
                                {tags.map(t => (
                                    <button
                                        key={t.tag_id}
                                        onClick={() => handleSelectByTag(t.tag_id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}
                                        onMouseEnter={e => e.target.style.background = 'var(--color-bg-base)'}
                                        onMouseLeave={e => e.target.style.background = 'transparent'}
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.tag_color || '#BDC1C6', flexShrink: 0 }} />
                                        {t.tag_name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Set Language bulk action */}
                    {selectedIds.size > 0 && (
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowSetLangDropdown(!showSetLangDropdown)}
                                style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #5B9FFF', borderRadius: 4, background: '#EBF4FF', cursor: 'pointer', color: '#1a56db', fontWeight: 500 }}
                            >Set Language ▾</button>
                            {showSetLangDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2, background: '#fff', border: '1px solid #DADCE0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: 160, padding: '4px 0' }}>
                                    {LANGUAGES.map(l => (
                                        <button
                                            key={l.code}
                                            onClick={() => handleSetLanguage(l.code)}
                                            style={{ display: 'block', width: '100%', padding: '6px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-base)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >{l.label}</button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Clear selection */}
                    {selectedIds.size > 0 && (
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #DADCE0', borderRadius: 4, background: 'var(--color-bg-base)', cursor: 'pointer', color: '#5F6368' }}
                        >Clear ({selectedIds.size})</button>
                    )}
                    {/* Delete selected */}
                    {selectedIds.size > 0 && (
                        <button
                            onClick={() => setConfirmDeleteCount(selectedIds.size)}
                            style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #ef4444', borderRadius: 4, background: '#FEF2F2', cursor: 'pointer', color: '#dc2626', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}
                        ><Trash2 size={12} /> Delete</button>
                    )}
                </div>

                {/* Delete confirmation dialog */}
                {confirmDeleteCount != null && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', padding: '10px 16px', borderRadius: 6, fontSize: 13, margin: '0 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#991B1B' }}>Are you sure you want to permanently delete <strong>{confirmDeleteCount}</strong> recipient{confirmDeleteCount !== 1 ? 's' : ''}? This action cannot be undone.</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={handleDeleteSelected}
                                style={{ padding: '4px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 4, background: '#DC2626', color: '#fff', cursor: 'pointer' }}
                            >Delete</button>
                            <button
                                onClick={() => setConfirmDeleteCount(null)}
                                style={{ padding: '4px 14px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151' }}
                            >Cancel</button>
                        </div>
                    </div>
                )}

                {/* Archive resync recovery — server staging area diverged from the local archive file */}
                {resyncConfirm && (
                    <ConfirmModal
                        open={true}
                        title="Archive out of sync"
                        message="The server’s staging area does not match the open archive file. This can happen after an interrupted archive run or after switching archive files. Resync the server to this file and retry?"
                        confirmLabel="Resync & Retry"
                        onConfirm={() => { resyncConfirm.resolve(true); setResyncConfirm(null); }}
                        onCancel={() => { resyncConfirm.resolve(false); setResyncConfirm(null); }}
                    />
                )}

                {/* Overwrite consent — recipient already present in the open archive (§A4) */}
                <ArchiveOverwriteDialog
                    open={!!overwriteConfirm}
                    collisions={overwriteConfirm?.collisions || []}
                    filename={overwriteConfirm?.filename}
                    onConfirm={() => { overwriteConfirm?.resolve?.(true); setOverwriteConfirm(null); }}
                    onCancel={() => { overwriteConfirm?.resolve?.(false); setOverwriteConfirm(null); }}
                />

                {/* BUG-111: CSV import confirmation — surfaces row count, duplicates, and column warnings before writing */}
                {csvImportConfirm && (
                    <ConfirmModal
                        open={true}
                        title={`Import ${csvImportConfirm.parsed.length} recipient(s) from CSV?`}
                        message={
                            <div style={{ fontSize: 13, color: '#3C4043', lineHeight: 1.6 }}>
                                <div>Rows read from the file: <strong>{csvImportConfirm.rowCount}</strong></div>
                                <div>Rows to import: <strong>{csvImportConfirm.parsed.length}</strong></div>
                                {/* Counted against the page on screen, so it is a floor,
                                    not a total. The real duplicate check runs on the
                                    server against the whole account. */}
                                <div>
                                    Already in the list: <strong>at least {csvImportConfirm.dupCount}</strong>
                                    <span style={{ color: '#5F6368' }}> (counted from the page on screen)</span>
                                </div>
                                {csvImportConfirm.errors?.length > 0 && (
                                    <details style={{ marginTop: 8 }} open>
                                        <summary style={{ cursor: 'pointer', color: '#C62828', fontWeight: 600 }}>
                                            {csvImportConfirm.errors.length} row{csvImportConfirm.errors.length !== 1 ? 's' : ''} will be skipped
                                        </summary>
                                        <ul style={{ margin: '6px 0 0', paddingLeft: 20, color: '#C62828' }}>
                                            {csvImportConfirm.errors.slice(0, 15).map((e, i) => <li key={i}>{e}</li>)}
                                            {csvImportConfirm.errors.length > 15 && (
                                                <li>…and {csvImportConfirm.errors.length - 15} more.</li>
                                            )}
                                        </ul>
                                    </details>
                                )}
                                {csvImportConfirm.warnings.length > 0 && (
                                    <details style={{ marginTop: 8 }}>
                                        <summary style={{ cursor: 'pointer', color: '#5F6368' }}>
                                            {csvImportConfirm.warnings.length} warning{csvImportConfirm.warnings.length !== 1 ? 's' : ''}
                                        </summary>
                                        <ul style={{ margin: '6px 0 0', paddingLeft: 20, color: '#5F6368' }}>
                                            {csvImportConfirm.warnings.slice(0, 15).map((w, i) => <li key={i}>{w}</li>)}
                                            {csvImportConfirm.warnings.length > 15 && (
                                                <li>…and {csvImportConfirm.warnings.length - 15} more.</li>
                                            )}
                                        </ul>
                                    </details>
                                )}
                                <div style={{ marginTop: 12, color: '#5F6368' }}>
                                    A recipient counts as a duplicate when the same email
                                    <strong> and</strong> the same name are already stored.
                                    <br />
                                    <strong>Import</strong> leaves those recipients exactly as they are.
                                    <strong> Overwrite</strong> updates them from the CSV. A blank cell
                                    still changes nothing, and neither choice can reverse an opt-out.
                                </div>
                                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                                    <button
                                        onClick={() => confirmCsvImport({ upsertDuplicates: true })}
                                        style={{ padding: '6px 14px', fontSize: 13, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                                    >Overwrite duplicates</button>
                                </div>
                            </div>
                        }
                        confirmLabel="Import (skip duplicates)"
                        cancelLabel="Cancel"
                        onConfirm={() => confirmCsvImport({ upsertDuplicates: false })}
                        onCancel={() => setCsvImportConfirm(null)}
                    />
                )}

                {/* Archive confirmation dialog */}
                {confirmArchiveCount != null && (
                    <div style={{ background: '#EBF4FF', border: '1px solid #BFDBFE', padding: '10px 16px', borderRadius: 6, fontSize: 13, margin: '0 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#1e3a5f' }}>Archive <strong>{confirmArchiveCount}</strong> recipient{confirmArchiveCount !== 1 ? 's' : ''}? This copies their data (including survey results) into the archive file. Nothing is removed — the recipients stay in the system exactly as they are.</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={handleArchiveSelected}
                                style={{ padding: '4px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 4, background: '#5B9FFF', color: '#fff', cursor: 'pointer' }}
                            >Archive</button>
                            <button
                                onClick={() => setConfirmArchiveCount(null)}
                                style={{ padding: '4px 14px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151' }}
                            >Cancel</button>
                        </div>
                    </div>
                )}

                {/* Language bulk-set result banner */}
                {langBulkResult && (
                    <div style={{ background: '#E8F5E9', color: '#2E7D32', padding: '8px 12px', borderRadius: 4, fontSize: 13, margin: '0 20px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><Check size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                            Set {langLabel(langBulkResult.lang)} for {langBulkResult.done} of {langBulkResult.total} recipient(s).
                        </span>
                        <button onClick={() => setLangBulkResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2E7D32', display: 'inline-flex' }}><X size={16} /></button>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div style={{ background: '#FFEBEE', color: '#C62828', padding: '8px 12px', borderRadius: 4, fontSize: 13, margin: '0 20px 8px' }}>
                        {error}
                    </div>
                )}

                {/* Contextual status overlay for long operations */}
                {progressMsg && (
                    <div style={styles.statusOverlay}>
                        <SamSpinner size={72} dark={false} text="Working" />
                        <div style={styles.statusOverlayText}>{progressMsg}</div>
                    </div>
                )}

                {/* Table */}
                <div style={styles.tableContainer}>
                    {/* Select-all-in-database banner */}
                    {allOnPageSelected && hasMorePages && !selectAllInDb && (
                        <div style={styles.selectAllBanner}>
                            All {recipients.length} recipients on this page are selected.{' '}
                            <button
                                onClick={() => setSelectAllInDb(true)}
                                style={styles.selectAllBannerBtn}
                            >
                                Select all {totalCount.toLocaleString()} recipients
                            </button>
                        </div>
                    )}
                    {selectAllInDb && (
                        <div style={{ ...styles.selectAllBanner, background: '#E8F5E9', borderColor: '#81C784' }}>
                            All {totalCount.toLocaleString()} recipients are selected.{' '}
                            <button
                                onClick={() => { setSelectAllInDb(false); setSelectedIds(new Set()); }}
                                style={{ ...styles.selectAllBannerBtn, color: '#2E7D32' }}
                            >
                                Clear selection
                            </button>
                        </div>
                    )}

                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 36 }}>
                                    <input
                                        type="checkbox"
                                        checked={selectAllInDb || (recipients.length > 0 && selectedIds.size === recipients.length)}
                                        onChange={() => { if (selectAllInDb) { setSelectAllInDb(false); setSelectedIds(new Set()); } else { toggleSelectAll(); } }}
                                    />
                                </th>
                                <th style={styles.th} onClick={() => handleSort('email')}>
                                    Email {sortBy === 'email' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('name')}>
                                    Name {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th style={styles.th}>Phone</th>
                                <th style={styles.th}>WhatsApp</th>
                                <th style={{ ...styles.th, minWidth: 175 }} title="Verified Messenger Page identities and the separate legacy ID">Messenger identities</th>
                                <th style={styles.th}>Group</th>
                                <th style={styles.th}>Channel</th>
                                <th style={styles.th}>Locale</th>
                                <th style={styles.th}>Language</th>
                                <th style={styles.th}>Timezone</th>
                                <th style={{ ...styles.th, minWidth: 100 }}>Tags</th>
                                <th style={styles.th}>Surveys</th>
                                <th style={styles.th}>First Seen</th>
                                <th style={styles.th}>Last Active</th>
                                <th style={styles.th}>Created</th>
                                <th style={{ ...styles.th, width: 72 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && recipients.length === 0 ? (
                                <tr><td colSpan={totalCols} style={styles.emptyCell}>Loading…</td></tr>
                            ) : recipients.length === 0 ? (
                                <tr><td colSpan={totalCols} style={styles.emptyCell}>No recipients found</td></tr>
                            ) : (
                                recipients.map(r => {
                                    const id = r.global_recipient_id;
                                    const sel = selectedIds.has(id);
                                    const isAlreadyStaged = stagedRecipientIds?.has(id);
                                    const isEditing = editingId === id;
                                    const rTags = recipientTagMap[id] || [];

                                    if (isEditing) {
                                        return (
                                            <tr key={id} style={{ ...styles.row, background: '#fffbeb' }}>
                                                <td style={styles.td}></td>
                                                <td style={styles.td}>{editInput('email', 'email')}</td>
                                                <td style={styles.td}>{editInput('name', 'name')}</td>
                                                <td style={styles.td}>{editInput('phone_number', 'phone')}</td>
                                                <td style={styles.td}>{editInput('whatsapp_id', 'whatsapp')}</td>
                                                <td style={styles.td}><MessengerIdentityCell recipient={r} legacyEditor={editInput('facebook_messenger_id', 'Legacy Messenger ID')} formatDate={fmtDate} /></td>
                                                <td style={styles.td}>{editInput('recipient_group', 'group')}</td>
                                                <td style={styles.td}>
                                                    <select style={styles.editInput} value={editFields.preferred_channel}
                                                        onChange={e => setEditFields(prev => ({ ...prev, preferred_channel: e.target.value }))}>
                                                        {CHANNELS.map(channel => <option key={channel.value} value={channel.value}>{channel.label}</option>)}
                                                    </select>
                                                </td>
                                                <td style={styles.td}>{editInput('locale', 'en-US')}</td>
                                                <td style={styles.td}>
                                                    <select style={styles.editInput} value={editFields.preferred_language || 'en'}
                                                        onChange={e => setEditFields(prev => ({ ...prev, preferred_language: e.target.value }))}>
                                                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                                                    </select>
                                                </td>
                                                <td style={styles.td}>{editInput('timezone', 'UTC')}</td>
                                                <td style={styles.td}>
                                                    <button onClick={() => openTagPanel(id)} style={styles.tagEditBtn} title="Edit tags">
                                                        <Tag size={14} />
                                                    </button>
                                                </td>
                                                <td style={{ ...styles.td, fontSize: 12, color: '#64748b' }}>
                                                    {r.total_surveys_completed}/{r.total_surveys_assigned}
                                                </td>
                                                {/* Read-only timestamps stay unchanged during edit */}
                                                <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.first_seen_at)}</td>
                                                <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.last_activity_at)}</td>
                                                <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.created_at)}</td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        <button onClick={saveEdit} style={styles.saveBtn} title="Save">
                                                            <Check size={14} />
                                                        </button>
                                                        <button onClick={() => setEditingId(null)} style={styles.cancelSmallBtn} title="Cancel">
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr
                                            key={id}
                                            style={{
                                                ...styles.row,
                                                background: sel ? '#eff6ff' : 'transparent',
                                                opacity: isAlreadyStaged ? 0.7 : 1,
                                            }}
                                            onClick={() => toggleSelect(id)}
                                        >
                                            <td style={styles.td}>
                                                <input type="checkbox" checked={sel} onChange={() => toggleSelect(id)} onClick={e => e.stopPropagation()} />
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: 500 }}>{r.email}</td>
                                            <td style={styles.td}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    {r.name || ''}
                                                    {isAlreadyStaged && (
                                                        <span style={styles.stagedBadge}>Staged</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{r.phone_number || '—'}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{r.whatsapp_id || '—'}</td>
                                            <td style={styles.td}><MessengerIdentityCell recipient={r} formatDate={fmtDate} /></td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{r.recipient_group || '—'}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{channelLabel(r.preferred_channel)}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{r.locale || '—'}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{langLabel(r.preferred_language)}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>{r.timezone || '—'}</td>
                                            <td style={{ ...styles.td, fontSize: 12 }}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                                                    {rTags.length > 0 ? rTags.map(rt => {
                                                        const info = getTagInfo(rt.tag_id);
                                                        const color = info?.tag_color || '#BDC1C6';
                                                        const name = info?.tag_name || rt.tag_name || '?';
                                                        return (
                                                            <span key={rt.tag_id} style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                                padding: '1px 7px', borderRadius: 9, fontSize: 11,
                                                                background: `${color}22`,
                                                                border: `1px solid ${color}`,
                                                                color: '#1e293b', fontWeight: 500,
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                                                                {name}
                                                            </span>
                                                        );
                                                    }) : (
                                                        <span style={{ color: '#DADCE0', fontSize: 11 }}>—</span>
                                                    )}
                                                    <button
                                                        onClick={e => { e.stopPropagation(); openTagPanel(id); }}
                                                        style={styles.tagEditBtn}
                                                        title="Manage tags"
                                                    ><Tag size={12} /></button>
                                                </div>
                                            </td>
                                            <td style={{ ...styles.td, fontSize: 12, color: '#64748b' }}>
                                                {r.total_surveys_completed}/{r.total_surveys_assigned}
                                            </td>
                                            <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.first_seen_at)}</td>
                                            <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.last_activity_at)}</td>
                                            <td style={{ ...styles.td, fontSize: 11, color: '#94a3b8' }}>{fmtDate(r.created_at)}</td>
                                            <td style={styles.td}>
                                                <button
                                                    onClick={e => { e.stopPropagation(); startEdit(r); }}
                                                    style={styles.editBtn}
                                                    title="Edit recipient"
                                                ><Pencil size={14} /></button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div style={styles.pagination}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                        style={styles.pageBtn}
                    >
                        <ChevronLeft size={14} style={{ verticalAlign: -2 }} /> Prev
                    </button>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                        Page {page} of {totalPages} · {totalCount.toLocaleString()} total
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || loading}
                        style={styles.pageBtn}
                    >
                        Next <ChevronRight size={14} style={{ verticalAlign: -2 }} />
                    </button>
                    <select
                        value={perPage}
                        onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                        style={styles.perPageSelect}
                    >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                    </select>
                </div>

                {/* ── Transfer footer bar ── Always visible */}
                <div style={styles.footer}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                        <ClipboardList size={16} color="#5B9FFF" />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                            {effectiveSelectedCount.toLocaleString()} selected{selectAllInDb ? ' (all)' : ''}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                            onClick={handleAddToStaging}
                            disabled={effectiveSelectedCount === 0}
                            style={{
                                ...styles.addToStagingBtn,
                                opacity: effectiveSelectedCount === 0 ? 0.55 : 1,
                                cursor: effectiveSelectedCount === 0 ? 'default' : 'pointer',
                            }}
                        >
                            Add to Staging
                        </button>
                        {selectedIds.size > 0 && (
                            <>
                                <button
                                    onClick={() => setSelectedIds(new Set())}
                                    style={styles.actionBtn}
                                >
                                    Clear
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '96vw', maxWidth: 1600,
        height: '90vh', display: 'flex', flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px', borderBottom: '1px solid #E8EAED',
    },
    badge: {
        background: '#eff6ff', color: '#5B9FFF', padding: '2px 10px',
        borderRadius: 12, fontSize: 13, fontWeight: 500,
    },
    headerBtn: {
        padding: '6px 14px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 13, cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center',
    },
    closeBtn: {
        background: 'transparent', border: 'none',
        cursor: 'pointer', padding: 4, color: '#5F6368', lineHeight: 1, display: 'inline-flex',
    },
    closeSmall: {
        background: 'transparent', border: 'none',
        cursor: 'pointer', color: '#BDC1C6', lineHeight: 1, display: 'inline-flex',
    },
    addForm: {
        padding: '12px 20px', borderBottom: '1px solid #E8EAED', background: '#E8F5E9',
    },
    formField: {
        display: 'flex', flexDirection: 'column', gap: 4,
    },
    formLabel: {
        fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px',
    },
    formInput: {
        padding: '6px 10px', borderRadius: 6, border: '1px solid #DADCE0', fontSize: 13, outline: 'none',
    },
    addBtn: {
        padding: '6px 16px', borderRadius: 6, border: 'none',
        background: '#5B9FFF', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    },
    cancelBtn: {
        padding: '6px 14px', borderRadius: 6, border: '1px solid #DADCE0',
        background: '#fff', fontSize: 13, cursor: 'pointer',
    },
    tagPanel: {
        padding: '12px 20px', borderBottom: '1px solid #E8EAED', background: '#faf5ff',
    },
    filterBar: {
        display: 'flex', gap: 8, padding: '12px 20px',
        borderBottom: '1px solid #F5F6F7', flexWrap: 'wrap', alignItems: 'center',
    },
    searchInput: {
        flex: 1, minWidth: 180, padding: '6px 10px', borderRadius: 6,
        border: '1px solid #DADCE0', fontSize: 13, outline: 'none',
    },
    select: {
        padding: '6px 8px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 13, background: '#fff', cursor: 'pointer',
    },
    actionBtn: {
        padding: '4px 12px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 12, cursor: 'pointer', fontWeight: 500, background: '#fff', display: 'inline-flex', alignItems: 'center',
    },
    tableContainer: {
        flex: 1, overflow: 'auto', padding: '0 20px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: 13,
    },
    th: {
        padding: '8px 4px', textAlign: 'left', borderBottom: '2px solid #E8EAED',
        cursor: 'pointer', fontWeight: 600, fontSize: 11, color: '#3C4043',
        position: 'sticky', top: 0, background: '#fff', userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    td: {
        padding: '5px 4px', borderBottom: '1px solid #F5F6F7', whiteSpace: 'nowrap',
    },
    row: {
        cursor: 'pointer', transition: 'background 0.1s',
    },
    emptyCell: {
        padding: 24, textAlign: 'center', color: '#BDC1C6',
    },
    editInput: {
        width: '100%', padding: '3px 5px', border: '1px solid #DADCE0', borderRadius: 4, fontSize: 12,
    },
    editBtn: {
        background: 'none', border: '1px solid #DADCE0', borderRadius: 4,
        padding: '3px 8px', cursor: 'pointer', color: '#5F6368', display: 'inline-flex', alignItems: 'center',
    },
    tagEditBtn: {
        background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', display: 'inline-flex', color: '#5B9FFF',
    },
    saveBtn: {
        background: '#E8F5E9', border: '1px solid #81C784', borderRadius: 4,
        padding: '3px 8px', cursor: 'pointer', color: '#2E7D32', display: 'inline-flex', alignItems: 'center',
    },
    cancelSmallBtn: {
        background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 4,
        padding: '3px 8px', cursor: 'pointer', color: '#991b1b', display: 'inline-flex', alignItems: 'center',
    },
    pagination: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: 16, padding: '12px 20px', borderTop: '1px solid #E8EAED',
    },
    pageBtn: {
        padding: '4px 12px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 12, cursor: 'pointer', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4,
    },
    perPageSelect: {
        padding: '4px 8px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 12, cursor: 'pointer', background: '#fff', color: '#64748b',
    },
    selectAllBanner: {
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
        padding: '8px 20px', background: '#EBF4FF', borderBottom: '1px solid #C6DAFC',
        fontSize: 13, color: '#1e40af',
    },
    selectAllBannerBtn: {
        background: 'none', border: 'none', color: '#1967D2', cursor: 'pointer',
        fontWeight: 600, textDecoration: 'underline', fontSize: 13, padding: 0,
    },
    statusOverlay: {
        position: 'absolute', inset: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.86)', backdropFilter: 'blur(1px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
        borderRadius: 12,
    },
    statusOverlayText: {
        fontSize: 14, fontWeight: 500, color: '#5F6368',
        textAlign: 'center', maxWidth: 380, lineHeight: 1.5,
    },
    footer: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 20px',
        borderTop: '1px solid #E8EAED',
        background: '#FAFBFC',
        borderRadius: '0 0 12px 12px',
    },
    stagedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: 10,
        background: '#F1F3F4',
        color: '#5F6368',
        border: '1px solid #DADCE0',
        fontSize: 10,
        fontWeight: 600,
        lineHeight: '14px',
    },
    addToStagingBtn: {
        padding: '6px 18px', borderRadius: 6, border: '1px solid #C6DAFC',
        background: '#E8F0FE', color: '#1967D2', fontSize: 13, fontWeight: 700,
    },
};
